package com.tovtalk.ui.screen.settings

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import com.tovtalk.ui.scaffold.ScreenScaffold
import com.tovtalk.ui.scaffold.TovTalkTopBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.text.font.FontWeight
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.User
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.ui.components.TovTalkAvatar
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.ui.components.CenteredContentWrapper
import com.tovtalk.utils.strings.StringProvider

@Composable
fun BlockedUsersScreen(
    onNavigateBack: () -> Unit,
    userRepository: UserRepository = koinInject()
) {
    var users by remember { mutableStateOf<List<User>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var lastId by remember { mutableStateOf<Int?>(null) }
    var hasMore by remember { mutableStateOf(true) }

    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        loading = true
        when (val r = userRepository.getBlockedUsers(limit = 20, lastId = null)) {
            is DomainResult.Success -> {
                users = r.data
                lastId = users.lastOrNull()?.id
                hasMore = users.size >= 20
            }
            else -> {}
        }
        loading = false
    }

    fun unblockUser(userId: Int) {
        scope.launch {
            when (userRepository.unblockUser(userId)) {
                is DomainResult.Success -> users = users.filter { it.id != userId }
                else -> {}
            }
        }
    }

    ScreenScaffold(
        topBar = {
            TovTalkTopBar(
                title = StringProvider.get("settings_blocked_users"),
                onBack = onNavigateBack,
            )
        },
        constrainContentWidth = false,
    ) { paddingValues ->
        val blockedUsersContent = @Composable {
        if (loading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator()
            }
        } else if (users.isEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = StringProvider.get("settings_blocked_empty"),
                    style = TovTalkTheme.typography.bodyLarge,
                    color = TovTalkTheme.colors.textSecondary
                )
            }
        } else {
            LazyColumn(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.xs)
            ) {
                items(users) { user ->
                    BlockedUserItem(
                        user = user,
                        onUnblock = { unblockUser(user.id) }
                    )
                }
            }
        }
        }
        CenteredContentWrapper { blockedUsersContent() }
    }
}

@Composable
private fun BlockedUserItem(
    user: User,
    onUnblock: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = TovTalkTheme.spacing.md, vertical = TovTalkTheme.spacing.sm),
        verticalAlignment = Alignment.CenterVertically
    ) {
        TovTalkAvatar(
            imageUrl = user.profilePhoto,
            name = user.fullname,
            size = 48.dp
        )
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(horizontal = TovTalkTheme.spacing.md)
        ) {
            Text(
                text = user.fullname,
                style = TovTalkTheme.typography.bodyLarge,
                fontWeight = FontWeight.Medium,
                color = TovTalkTheme.colors.textPrimary
            )
            Text(
                text = "@${user.username}",
                style = TovTalkTheme.typography.bodySmall,
                color = TovTalkTheme.colors.textSecondary
            )
        }
        TextButton(onClick = onUnblock) {
            Text(
                text = StringProvider.get("settings_unblock"),
                color = TovTalkTheme.colors.brand
            )
        }
    }
}
